/**
 * One rule for a contact's three name fields, used by every form that writes a
 * contact (owner, 2026-10-16: "Full name, First name and Last name … full
 * consistency").
 *
 *   firstName  required on every form
 *   lastName   optional
 *   name       the FULL name; the server requires it, so a form always sends it.
 *
 * 2026-10-16 (owner: "if one is filled other 2 get auto filled and vice versa"):
 * the fields fill EACH OTHER both ways through `applyNameEdit` below — use it,
 * not the older one-way `fullNameFollowsParts` + touched-flag pattern.
 *
 * Splitting a full name back into parts is a GUESS (see backend migration 041),
 * so `splitFullName` is used only to PREFILL a form from a single-string source
 * (an enquiry's contact name, a legacy row with no parts). The user sees and can
 * correct the result before anything is saved.
 */

/**
 * Trim and collapse internal whitespace runs to one space, matching the
 * server's `normaliseSpaces` so the client and server agree on "follows".
 */
function normaliseSpaces(value: string | null | undefined): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

/** First + last, single-spaced and trimmed. '' when both are blank. */
export function composeFullName(firstName: string | null | undefined, lastName: string | null | undefined): string {
  return [firstName, lastName]
    .map((part) => normaliseSpaces(part))
    .filter((part) => part !== '')
    .join(' ');
}

/**
 * Prefill guess: the last word is the last name, everything before it the first.
 * A single word is a first name only.
 */
export function splitFullName(fullName: string | null | undefined): { firstName: string; lastName: string } {
  const words = (fullName ?? '').trim().split(/\s+/).filter((word) => word !== '');
  if (words.length === 0) return { firstName: '', lastName: '' };
  if (words.length === 1) return { firstName: words[0] ?? '', lastName: '' };
  return { firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] ?? '' };
}

/**
 * Whether the full name is still FOLLOWING the parts, i.e. has not been edited
 * by hand. A form keeps a `fullNameTouched` flag seeded from this on load:
 * a stored full name equal to first + last (or empty) keeps following.
 */
export function fullNameFollowsParts(
  fullName: string | null | undefined,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): boolean {
  const full = normaliseSpaces(fullName);
  return full === '' || full === normaliseSpaces(composeFullName(firstName, lastName));
}

export interface ContactNameParts {
  firstName: string;
  lastName: string;
  /** The full name. */
  name: string;
}

export type ContactNameField = keyof ContactNameParts;

/**
 * Whether the three fields currently AGREE: the full name is first + last
 * (whitespace-normalised), or one side is entirely blank. Agreement is what
 * lets an edit on one side carry to the other.
 */
export function namesInSync(current: ContactNameParts): boolean {
  const full = normaliseSpaces(current.name);
  const composed = composeFullName(current.firstName, current.lastName);
  return full === '' || composed === '' || full === composed;
}

/**
 * THE TWO-WAY RULE (owner, 2026-10-16: "if one is filled other 2 get auto
 * filled and vice versa"). Stateless — no "touched" flags — so every surface
 * that edits a contact name behaves identically:
 *
 *   type in First or Last name  → Full name becomes first + last,
 *                                  IF the three agreed before this keystroke
 *   type in Full name           → First / Last become its split (last word is
 *                                  the last name), IF they agreed before
 *   they did NOT agree          → only the typed field changes. That is how a
 *                                  hand-made full name ("Dr. A. R. Menon" with
 *                                  parts "A. R." / "Menon") is kept: nothing
 *                                  overwrites a deliberate difference.
 *
 * `split(compose(x))` round-trips, so typing a full name keeps the fields in
 * agreement keystroke by keystroke and the carry continues.
 *
 * The typed value is returned AS TYPED (no trimming) so the caret never jumps;
 * the derived fields are normalised.
 */
export function applyNameEdit(
  current: ContactNameParts,
  field: ContactNameField,
  value: string,
): ContactNameParts {
  const next: ContactNameParts = { ...current, [field]: value };
  if (!namesInSync(current)) return next;
  if (field === 'name') {
    const parts = splitFullName(value);
    return { ...next, firstName: parts.firstName, lastName: parts.lastName };
  }
  return { ...next, name: composeFullName(next.firstName, next.lastName) };
}

export const CONTACT_NAME_LABELS = {
  firstName: 'First name',
  lastName: 'Last name',
  name: 'Full name',
} as const;
